import set from 'lodash/set';
import omitBy from 'lodash/omitBy';
import type { LocationQueryRaw } from 'vue-router';
import type { Pagination } from '~/types/ui';

export const useCreateFilters = async <T extends Record<string, any>>(filters: T) => {
  const route = useRoute();
  const router = useRouter();

  await router.isReady();

  const filtersObj = {} as T;

  Object.entries({ ...filters }).forEach(([key, value]) => {
    let queryValue = route.query[key] as string | number[] | string[];

    if (key === 'query' && !queryValue && route.query.search) {
      queryValue = route.query.search as string;
    }

    if (Array.isArray(queryValue)) {
      queryValue = queryValue.map((v) => (+v ? Number(v) : v)) as number[];
    } else if (queryValue && Array.isArray(value)) {
      queryValue = +queryValue ? [Number(queryValue)] : [queryValue];
    }

    // Boolean filters need explicit coercion: route.query values are strings,
    // so `queryValue || value` would resolve URL `?developer_vault_only=false`
    // to the TRUTHY string "false". The historical workaround was to manually
    // override the affected fields after this composable ran (e.g. zeroing
    // `exclusive` and `favorites_only` in explore.vue) — which is why
    // `developer_vault_only` kept sneaking on through back-nav and silently
    // flipped the page into the showcase data source (~11 results instead
    // of full ~3K).
    if (typeof value === 'boolean') {
      const resolved =
        queryValue === undefined ? value : queryValue === true || queryValue === 'true' || queryValue === '1';
      set(filtersObj, key, resolved);
      return;
    }

    set(filtersObj, key, queryValue || value);
  });

  return ref<T>(filtersObj);
};

export const useParseFilters = <T extends Record<string, any>>(
  filters: Ref<T>,
  paginationData?: Ref<Pagination>,
  applyToRoute = true,
  // Optional URL shaping for the route write (e.g. explore's `exclusive=1`); the params used by queries are unchanged.
  toRouteQuery: (query: Record<string, unknown>) => Record<string, unknown> = (query) => query,
) => {
  const router = useRouter();

  const params = computed(() => ({
    ...omitBy<T>(filters.value, (f) => f === undefined || f === null || f === '' || (Array.isArray(f) && !f.length)),
    ...(paginationData?.value
      ? {
          page: paginationData.value.current_page,
          per_page: paginationData.value.per_page,
        }
      : {}),
  }));

  if (applyToRoute) {
    const hash = router.currentRoute.value?.hash;
    if (hash !== undefined) {
      router.replace({ query: params.value, hash }).catch(() => {});
    } else {
      router.replace({ query: params.value }).catch(() => {});
    }
  }

  const route = useRoute();

  const applyParams = () => {
    nextTick(() => {
      const paywallForce = route.query?.paywall_force;
      const paywallVersion = route.query?.paywall_version;
      const freeTrial = route.query?.free_trial;
      const finalQuery = toRouteQuery({ ...params.value }) as LocationQueryRaw;
      if (paywallForce) {
        finalQuery.paywall_force = paywallForce;
      }
      if (paywallVersion) {
        finalQuery.paywall_version = paywallVersion;
      }
      if (freeTrial) {
        finalQuery.free_trial = freeTrial;
      }

      if (route && route.hash !== undefined) {
        router.replace({ query: finalQuery, hash: route.hash }).catch(() => {});
      } else {
        router.replace({ query: finalQuery }).catch(() => {});
      }
    });
  };

  const queryKey = ref<typeof params.value | null>(null);

  // @ts-ignore
  queryKey.value = params.value;

  return {
    params,
    applyParams,
    queryKey,
  };
};

export const useCountFilters = (filters: object, excludeKeys: string[] = []) => {
  const mandatoryExcludeKeys = ['orderBy', 'sortedBy', 'page', 'limit', ...excludeKeys];

  let count = 0;

  Object.entries(filters)
    .filter(([k]) => !mandatoryExcludeKeys.includes(k))
    .forEach(([_, value]) => {
      if (Array.isArray(value)) {
        if (value.length) count++;
      } else if (value !== undefined && value !== null && value !== '') {
        count++;
      }
    });
  return count;
};
